/**
 * Error Logging Service
 * Captures runtime and API errors locally and can forward them to backend.
 */

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://kenluk-backend-production.up.railway.app/api';

export interface ErrorLog {
  timestamp: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  stack?: string;
  context?: Record<string, any>;
  userAgent?: string;
  url?: string;
  userId?: string;
  requestId?: string;
  statusCode?: number;
  path?: string;
  method?: string;
}

interface SendLogsOptions {
  userId?: string;
  note?: string;
  includeAllLogs?: boolean;
}

interface SendLogsResult {
  success: boolean;
  message: string;
  reportId?: string;
}

class ErrorLogger {
  private logs: ErrorLog[] = [];
  private readonly MAX_LOGS = 100;
  private readonly LOG_KEY = 'payment_app_logs';

  constructor() {
    this.loadLogsFromStorage();
  }

  logError(message: string, error?: any, context?: Record<string, any>) {
    const requestId =
      context?.requestId ||
      error?.response?.data?.requestId ||
      error?.response?.headers?.['x-request-id'];

    const statusCode = context?.statusCode || error?.response?.status;
    const path = context?.path || error?.config?.url;
    const method = context?.method || error?.config?.method?.toUpperCase?.();

    const errorLog: ErrorLog = {
      timestamp: new Date().toISOString(),
      severity: 'error',
      message,
      stack: error?.stack || '',
      context: {
        ...context,
        errorName: error?.name,
        errorMessage: error?.message,
      },
      userAgent: this.getUserAgent(),
      url: this.getLocationHref(),
      requestId,
      statusCode,
      path,
      method,
    };

    this.addLog(errorLog);
    console.error(`[ERROR] ${message}`, error, context);
  }

  logWarning(message: string, context?: Record<string, any>) {
    const warningLog: ErrorLog = {
      timestamp: new Date().toISOString(),
      severity: 'warning',
      message,
      context,
      userAgent: this.getUserAgent(),
      url: this.getLocationHref(),
      requestId: context?.requestId,
      statusCode: context?.statusCode,
      path: context?.path,
      method: context?.method,
    };

    this.addLog(warningLog);
    console.warn(`[WARNING] ${message}`, context);
  }

  logInfo(message: string, context?: Record<string, any>) {
    const infoLog: ErrorLog = {
      timestamp: new Date().toISOString(),
      severity: 'info',
      message,
      context,
      userAgent: this.getUserAgent(),
      url: this.getLocationHref(),
      requestId: context?.requestId,
      statusCode: context?.statusCode,
      path: context?.path,
      method: context?.method,
    };

    this.addLog(infoLog);
    console.log(`[INFO] ${message}`, context);
  }

  private addLog(log: ErrorLog) {
    this.logs.push(log);
    if (this.logs.length > this.MAX_LOGS) {
      this.logs = this.logs.slice(-this.MAX_LOGS);
    }
    this.saveLogsToStorage();
  }

  private saveLogsToStorage() {
    try {
      localStorage.setItem(this.LOG_KEY, JSON.stringify(this.logs));
    } catch (error) {
      console.warn('Failed to save logs to localStorage', error);
    }
  }

  private loadLogsFromStorage() {
    try {
      const stored = localStorage.getItem(this.LOG_KEY);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to load logs from localStorage', error);
    }
  }

  private getUserAgent() {
    if (typeof navigator === 'undefined') {
      return 'unknown';
    }
    return navigator.userAgent;
  }

  private getLocationHref() {
    if (typeof window === 'undefined') {
      return 'unknown';
    }
    return window.location.href;
  }

  private getSessionUserId() {
    try {
      const raw = sessionStorage.getItem('user');
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      return parsed?.id;
    } catch (error) {
      return undefined;
    }
  }

  getLogs(): ErrorLog[] {
    return [...this.logs];
  }

  getFormattedLogs(): string {
    return this.logs
      .map((log) => {
        return [
          `[${log.timestamp}] ${log.severity.toUpperCase()}: ${log.message}`,
          log.requestId ? `Request ID: ${log.requestId}` : '',
          log.statusCode ? `Status: ${log.statusCode}` : '',
          log.path ? `Path: ${log.path}` : '',
          log.context ? `Context: ${JSON.stringify(log.context, null, 2)}` : '',
          log.stack ? `Stack: ${log.stack}` : '',
        ]
          .filter(Boolean)
          .join('\n');
      })
      .join('\n---\n');
  }

  exportLogsAsFile() {
    const dataStr = JSON.stringify(this.logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payment-app-logs-${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async sendLogsToBackend(options?: SendLogsOptions): Promise<SendLogsResult> {
    const userId = options?.userId || this.getSessionUserId();
    const logsToSend = options?.includeAllLogs ? [...this.logs] : this.logs.slice(-30);
    const token = sessionStorage.getItem('token');

    if (logsToSend.length === 0) {
      return {
        success: false,
        message: 'No logs available to submit',
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/logs/client-errors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          note: options?.note || '',
          userId,
          logs: logsToSend,
          exportedAt: new Date().toISOString(),
          source: 'web-client',
        }),
      });

      const responseData = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          responseData?.message || `Failed to send logs (HTTP ${response.status})`
        );
      }

      const reportId = responseData?.data?.reportId;
      this.logInfo('Client error logs sent to backend', { reportId, count: logsToSend.length });

      return {
        success: true,
        message: responseData?.message || 'Logs sent successfully',
        reportId,
      };
    } catch (error: any) {
      this.logError('Failed to send logs to backend', error, {
        path: '/logs/client-errors',
        method: 'POST',
      });
      return {
        success: false,
        message: error?.message || 'Failed to send logs to backend',
      };
    }
  }

  clearLogs() {
    this.logs = [];
    localStorage.removeItem(this.LOG_KEY);
  }

  getRecentErrors(count: number = 10): ErrorLog[] {
    return this.logs
      .filter((log) => log.severity === 'error')
      .slice(-count)
      .reverse();
  }

  getLogsBySeverity(severity: 'error' | 'warning' | 'info'): ErrorLog[] {
    return this.logs.filter((log) => log.severity === severity);
  }
}

export const errorLogger = new ErrorLogger();
(window as any).errorLogger = errorLogger;

export function setupGlobalErrorHandlers() {
  const appWindow = window as any;
  if (appWindow.__errorHandlersInstalled) {
    return;
  }
  appWindow.__errorHandlersInstalled = true;

  window.addEventListener('error', (event) => {
    errorLogger.logError('Uncaught Error', event.error, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    errorLogger.logError('Unhandled Promise Rejection', event.reason, {
      promise: event.promise,
    });
  });
}
